import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { User, Mail, Phone, Calendar, MapPin, Building2, Lock, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authApi } from '../../lib/api/auth';
import { UserRole as BackendUserRole, Gender } from '../../lib/api/types';
import { useHospitals } from '../../lib/hooks/useHospitals';

interface RegisterPatientModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  userRole: 'coordinator' | 'physician_assistant';
}

export function RegisterPatientModal({ open, onOpenChange, onSuccess, userRole }: RegisterPatientModalProps) {
  const [step, setStep] = useState(1); // 1: Demographics, 2: Contact & Hospital, 3: Password
  
  // Fetch hospitals from API
  const { data: hospitals = [], isLoading: hospitalsLoading } = useHospitals({ is_active: true });
  
  // Form fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');
  const [hospital, setHospital] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setStep(1);
    setFullName('');
    setEmail('');
    setPhoneNumber('');
    setDateOfBirth('');
    setGender('');
    setAddress('');
    setCity('');
    setPincode('');
    setHospital('');
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setIsLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const validateStep1 = () => {
    if (!fullName.trim()) {
      setError('Please enter patient\'s full name');
      return false;
    }
    if (!dateOfBirth) {
      setError('Please select date of birth');
      return false;
    }
    if (!gender) {
      setError('Please select gender');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!email.trim()) {
      setError('Please enter email address');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!phoneNumber.trim()) {
      setError('Please enter phone number');
      return false;
    }
    const phoneRegex = /^[\d\s\+\-()]+$/;
    if (!phoneRegex.test(phoneNumber.trim())) {
      setError('Please enter a valid phone number');
      return false;
    }
    if (!address.trim()) {
      setError('Please enter address');
      return false;
    }
    if (!city.trim()) {
      setError('Please enter city');
      return false;
    }
    if (!pincode.trim()) {
      setError('Please enter pincode');
      return false;
    }
    if (!hospital) {
      setError('Please select a hospital');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!password) {
      setError('Please enter a password');
      return false;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (!confirmPassword) {
      setError('Please confirm password');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError(null);
    
    if (step === 1) {
      if (validateStep1()) {
        setStep(2);
      } else {
        toast.error(error || 'Please complete all fields');
      }
    } else if (step === 2) {
      if (validateStep2()) {
        setStep(3);
      } else {
        toast.error(error || 'Please complete all fields');
      }
    } else if (step === 3) {
      handleRegister();
    }
  };

  const handleBack = () => {
    setError(null);
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleRegister = async () => {
    setError(null);
    
    if (!validateStep3()) {
      toast.error(error || 'Please complete all fields');
      return;
    }

    setIsLoading(true);
    
    try {
      // Split full name into first and last name
      const nameParts = fullName.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || nameParts[0];

      // Map gender to backend Gender enum
      const genderMap: Record<string, Gender> = {
        'male': Gender.MALE,
        'female': Gender.FEMALE,
        'other': Gender.OTHER,
      };

      // Register patient
      await authApi.register({
        email: email.trim(),
        password: password,
        first_name: firstName,
        last_name: lastName,
        phone: phoneNumber.trim(),
        role: BackendUserRole.PATIENT,
        date_of_birth: dateOfBirth,
        gender: genderMap[gender],
        address: `${address}, ${city}, ${pincode}`,
        city: city,
        pincode: pincode,
        hospital_id: hospital ? parseInt(hospital) : undefined,
      });

      toast.success('Patient registered successfully!', {
        description: `${fullName} has been registered and can now log in.`,
      });

      // Reset form and close modal
      handleClose();
      
      // Callback for parent to refresh data
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to register patient:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to register patient. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary">
            <User className="w-5 h-5" />
            Register New Patient
          </DialogTitle>
          <DialogDescription>
            Step {step} of 3: {step === 1 ? 'Personal Information' : step === 2 ? 'Contact & Hospital' : 'Set Password'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-6 py-4">
          {/* Step 1: Demographics */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="fullName" className="flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />
                  Full Name *
                </Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter patient's full name"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dateOfBirth" className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    Date of Birth *
                  </Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="mt-1"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div>
                  <Label htmlFor="gender" className="flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    Gender *
                  </Label>
                  <Select value={gender} onValueChange={setGender}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Contact & Hospital */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary" />
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="patient@example.com"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="phoneNumber" className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    Phone Number *
                  </Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+91 98765 43210"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  Address *
                </Label>
                <Input
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Street address"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="pincode">Pincode *</Label>
                  <Input
                    id="pincode"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                    placeholder="400001"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="hospital" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-primary" />
                  Hospital *
                </Label>
                <Select value={hospital} onValueChange={setHospital}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={hospitalsLoading ? "Loading hospitals..." : "Select hospital"} />
                  </SelectTrigger>
                  <SelectContent>
                    {hospitals.map((h: any) => (
                      <SelectItem key={h.id} value={h.id.toString()}>
                        {h.name} - {h.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 3: Password */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-primary" />
                  Password *
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Password must be at least 8 characters long
                </p>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-primary" />
                  Confirm Password *
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  className="mt-1"
                />
              </div>

              {/* Summary */}
              <div className="mt-6 p-4 bg-muted/50 rounded-lg space-y-2">
                <p className="text-sm font-medium text-primary">Registration Summary</p>
                <div className="text-sm space-y-1">
                  <p><strong>Name:</strong> {fullName}</p>
                  <p><strong>Email:</strong> {email}</p>
                  <p><strong>Phone:</strong> {phoneNumber}</p>
                  <p><strong>DOB:</strong> {dateOfBirth}</p>
                  <p><strong>Gender:</strong> {gender}</p>
                  <p><strong>City:</strong> {city}</p>
                  <p><strong>Hospital:</strong> {hospitals.find((h: any) => h.id.toString() === hospital)?.name || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex gap-2">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={isLoading}
              >
                Back
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
          
          <Button
            onClick={handleNext}
            disabled={isLoading}
            className="bg-primary hover:bg-primary/90"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Registering...
              </>
            ) : step === 3 ? (
              'Register Patient'
            ) : (
              'Next'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
